import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Bookmark, Search, ExternalLink, ArrowRight, CalendarDays } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { toast } from "sonner";

const PUBLIC_LABEL = { Book: "Books", Article: "Temple Journal", Reflection: "Reflections", "Study Resource": "Additional Reading" };
const FILTERS = ["All", "Book", "Article", "Reflection", "Study Resource"];
const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };
const fmtDate = (d) => { if (!d) return null; const t = new Date(d); return isNaN(t) ? d : t.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); };
const isWatchers = (l) => /watchers are still watching/i.test(l?.title || "");
const paragraphs = (text) => {
  const t = (text || "").trim();
  if (!t) return [];
  if (/\n\s*\n/.test(t)) return t.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const sentences = t.match(/[^.!?]+[.!?]+["”']?(\s+|$)/g) || [t];
  const out = [];
  for (let i = 0; i < sentences.length; i += 2) out.push(sentences.slice(i, i + 2).join("").trim());
  return out;
};

export const isUpcoming = (l) => l.type === "Book" && l.availability === "upcoming";
export const opensInside = (l) => l.type !== "Book" && !!(l.body || "").trim();

export default function Read() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const { toggleSave, isSaved, user, authLoading } = useApp();
  const [sp, setSp] = useSearchParams();
  const returnBookId = sp.get("book");

  useEffect(() => {
    let ignore = false;
    (async () => {
      try { const { data } = await api.get("/content/library"); if (!ignore) setItems(data || []); }
      catch {} finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!returnBookId || loading || authLoading) return;
    const book = items.find((l) => l.id === returnBookId && l.type === "Book");
    if (book && user) { setOpen(book); setSp({}, { replace: true }); }
    else if (book && !user) navigate(`/login?next=${encodeURIComponent(`/read?book=${book.id}`)}`);
    else setSp({}, { replace: true });
  }, [returnBookId, loading, authLoading, items, user, navigate, setSp]);

  const match = (l) => q === "" || (l.title || "").toLowerCase().includes(q.toLowerCase()) || (l.author || "").toLowerCase().includes(q.toLowerCase());
  const visible = items.filter(match);
  const featured = visible.find((l) => l.featured && isUpcoming(l)) || visible.find((l) => l.featured);
  const groups = FILTERS.slice(1).filter((t) => filter === "All" || filter === t)
    .map((t) => ({ type: t, rows: visible.filter((l) => l.type === t && l.id !== featured?.id) })).filter((g) => g.rows.length);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="read-page">
      <div className="mb-10 rise">
        <div className="eyebrow mb-2">The Library</div>
        <h1 className="font-serif text-4xl sm:text-5xl" style={{ color: "var(--burgundy)" }}>Words that open the chamber.</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          Books, articles and reflections composed for the contemplative reader — meant to be lingered with, not consumed.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <div className="flex items-center gap-2 rounded-full border px-4 py-2 w-full sm:w-72" style={card}>
          <Search className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <input data-testid="library-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the library…" className="bg-transparent text-sm outline-none flex-1" />
        </div>
        <div className="inline-flex p-1 rounded-full border flex-wrap" style={card}>
          {FILTERS.map((f) => (
            <button key={f} data-testid={`library-filter-${f.toLowerCase().replace(" ", "-")}-btn`} onClick={() => setFilter(f)}
              className="px-4 py-1.5 rounded-full text-xs font-medium"
              style={{ background: filter === f ? "var(--burgundy)" : "transparent", color: filter === f ? "var(--bg-cream)" : "var(--text-obsidian)" }}>
              {f === "All" ? "All" : PUBLIC_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      {loading ? null : visible.length === 0 ? (
        <div className="rounded-2xl border p-10 text-center" style={card}>
          <div className="eyebrow mb-2">Coming soon</div>
          <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>New library offerings are being prepared.</p>
        </div>
      ) : (
        <>
          {featured && filter === "All" && <FeaturedItem item={featured} onOpen={setOpen} />}
          {groups.map((g) => (
            <section key={g.type} className="mb-14" data-testid={`read-section-${g.type.toLowerCase().replace(" ", "-")}`}>
              <div className="flex items-baseline gap-4 mb-6">
                <h2 className="eyebrow">{PUBLIC_LABEL[g.type]}</h2>
                <span className="h-px flex-1" style={{ background: "var(--border-cream)" }} />
              </div>
              <div className={`grid gap-6 sm:grid-cols-2 ${g.type === "Book" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
                {g.rows.map((l) => g.type === "Book" ? <BookCard key={l.id} l={l} onOpen={setOpen} /> : <ItemCard key={l.id} l={l} onOpen={setOpen} />)}
              </div>
            </section>
          ))}
        </>
      )}

      {open && <ReaderModal item={open} onClose={() => setOpen(null)} toggleSave={toggleSave} isSaved={isSaved} />}
    </div>
  );
}

function BookAction({ l, small }) {
  const { user } = useApp();
  const navigate = useNavigate();
  if (isUpcoming(l)) return (
    <span data-testid={`book-upcoming-${l.id}`} className={`inline-flex items-center gap-1.5 rounded-full ${small ? "px-3 py-1 text-[11px]" : "px-4 py-2 text-sm"}`} style={{ background: "var(--teal-tint)", color: "var(--teal-deep)" }}>
      <CalendarDays className="w-3.5 h-3.5" /> {l.release_date ? `Coming ${fmtDate(l.release_date)}` : "Coming soon"}
    </span>
  );
  if (!l.external_url) return null;
  const cls = `inline-flex items-center gap-1.5 rounded-full font-medium ${small ? "px-3 py-1 text-[11px]" : "px-4 py-2 text-sm"}`;
  if (!user) return (
    <button data-testid={`book-buy-${l.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/login?next=${encodeURIComponent(`/read?book=${l.id}`)}`); }}
      className={cls} style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} title="Sign in or create a free account to continue to Amazon">
      <ExternalLink className="w-3.5 h-3.5" /> Get the book · Sign in
    </button>
  );
  return (
    <a data-testid={`book-buy-${l.id}`} href={l.external_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className={cls} style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
      <ExternalLink className="w-3.5 h-3.5" /> Get the book
    </a>
  );
}

function BookCard({ l, onOpen }) {
  return (
    <div data-testid={`library-item-${l.id}-btn`} role="button" tabIndex={0} onClick={() => onOpen(l)} onKeyDown={(e) => e.key === "Enter" && onOpen(l)}
      className="lift text-left rounded-2xl overflow-hidden border cursor-pointer flex flex-col" style={card}>
      <div className="aspect-[2/3] w-full flex items-center justify-center" style={{ background: "var(--teal-tint)" }}>
        {l.art_url ? <img src={assetUrl(l.art_url)} alt={l.title} className="w-full h-full object-cover" /> : <BookOpen className="w-10 h-10" style={{ color: "var(--teal)" }} />}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {isUpcoming(l) && <div className="eyebrow text-[9px] mb-1" style={{ color: "var(--teal)" }}>Upcoming</div>}
        <h3 className="font-serif text-lg leading-tight" style={{ color: "var(--burgundy)" }}>{l.title}</h3>
        {l.subtitle && <p className="text-xs mt-1 italic" style={{ color: "var(--text-muted)" }}>{l.subtitle}</p>}
        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{l.author}</div>
        <div className="mt-3 pt-1 mt-auto"><BookAction l={l} small /></div>
      </div>
    </div>
  );
}

function ItemCard({ l, onOpen }) {
  const inner = (
    <>
      {l.art_url && (
        <div className="relative h-52">
          <img src={assetUrl(l.art_url)} alt={l.title} className="w-full h-full object-cover" />
          <span className="absolute top-3 left-3 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>{PUBLIC_LABEL[l.type]}</span>
        </div>
      )}
      <div className="p-5">
        <h3 className="font-serif text-xl leading-tight" style={{ color: "var(--burgundy)" }}>{l.title}</h3>
        {l.subtitle && <p className="text-sm mt-1 italic" style={{ color: "var(--text-muted)" }}>{l.subtitle}</p>}
        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{l.author}{l.length ? ` · ${l.length}` : ""}</div>
        {(l.excerpt || l.description) && <p className="text-sm mt-3 leading-relaxed italic" style={{ color: "var(--text-obsidian)" }}>"{l.excerpt || l.description}"</p>}
        {opensInside(l) && <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--burgundy)" }}>Read <ArrowRight className="w-3.5 h-3.5" /></div>}
      </div>
    </>
  );
  const cls = "lift text-left rounded-2xl overflow-hidden border block";
  return opensInside(l)
    ? <Link to={`/read/${l.id}`} data-testid={`library-item-${l.id}-btn`} className={cls} style={card}>{inner}</Link>
    : <button data-testid={`library-item-${l.id}-btn`} onClick={() => onOpen(l)} className={cls + " w-full"} style={card}>{inner}</button>;
}

function FeaturedItem({ item: l, onOpen }) {
  return (
    <section className="mb-14 rounded-3xl border overflow-hidden grid md:grid-cols-5" style={card} data-testid="read-featured">
      <div className={`md:col-span-2 ${l.type === "Book" ? "aspect-[2/3] md:aspect-auto" : "h-64 md:h-auto"}`} style={{ background: "var(--teal-tint)" }}>
        {l.art_url && <img src={assetUrl(l.art_url)} alt={l.title} className="w-full h-full object-cover" />}
      </div>
      <div className="md:col-span-3 p-7 sm:p-10 flex flex-col justify-center">
        <div className="eyebrow mb-3" style={{ color: isUpcoming(l) ? "var(--teal)" : undefined }}>{isUpcoming(l) ? "Upcoming book" : `Featured · ${PUBLIC_LABEL[l.type]}`}</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--burgundy)" }}>{l.title}</h2>
        {l.subtitle && <p className="font-serif italic text-lg mt-2" style={{ color: "var(--text-muted)" }}>{l.subtitle}</p>}
        <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{l.author}{l.length ? ` · ${l.length}` : ""}</div>
        {(l.description || l.excerpt) && (
          <div className="mt-5 max-w-xl space-y-3" data-testid="featured-description">
            {paragraphs(l.description || l.excerpt).map((p, i) => <p key={i} className="text-base leading-relaxed" style={{ color: "var(--text-obsidian)" }}>{p}</p>)}
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {l.type === "Book" ? <BookAction l={l} /> : opensInside(l)
            ? <Link to={`/read/${l.id}`} data-testid="featured-read-link" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Read <ArrowRight className="w-4 h-4" /></Link>
            : <button onClick={() => onOpen(l)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Open</button>}
          {l.type === "Book" && isUpcoming(l) && l.external_url && !isWatchers(l) && <a href={l.external_url} target="_blank" rel="noreferrer" className="text-sm ink-link" style={{ color: "var(--burgundy)" }}>Learn more →</a>}
        </div>
        {isWatchers(l) && <WatchersGate />}
      </div>
    </section>
  );
}

function WatchersGate() {
  const { user, authLoading } = useApp();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const autoClaim = sp.get("claim") === "watchers";

  useEffect(() => { api.get("/watchers/status").then((r) => setStatus(r.data)).catch(() => setStatus({ open: false })); }, []);
  useEffect(() => { if (user) api.get("/me/watchers").then((r) => setMine(r.data)).catch(() => setMine(null)); else setMine(null); }, [user]);

  const claim = React.useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/watchers/claim");
      setMine({ claimed: true, claimed_at: data.claimed_at, preview_url: data.preview_url });
      toast(data.already ? "You're already through the gate — check your email for Chapters 1–5." : "The gate is open. Chapters 1–5 are on their way to your inbox.");
      api.get("/watchers/status").then((r) => setStatus(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to open the gate right now.");
      api.get("/watchers/status").then((r) => setStatus(r.data)).catch(() => {});
    } finally { setBusy(false); }
  }, [busy]);

  useEffect(() => {
    if (autoClaim && user && !authLoading && mine && !mine.claimed && status?.open) { setSp({}, { replace: true }); claim(); }
    else if (autoClaim && user && mine?.claimed) setSp({}, { replace: true });
  }, [autoClaim, user, authLoading, mine, status, claim, setSp]);

  const onOpen = () => {
    if (!user) { navigate("/login?next=" + encodeURIComponent("/read?claim=watchers")); return; }
    claim();
  };

  if (!status) return null;
  const box = { background: "var(--surface-deep)", color: "var(--bg-cream)" };
  if (mine?.claimed) return (
    <div className="mt-6 rounded-2xl p-6" style={box} data-testid="watchers-gate-claimed">
      <div className="eyebrow mb-1" style={{ color: "var(--gold-soft)" }}>🔓 You are through the gate</div>
      <p className="font-serif text-lg leading-snug">Your early-access preview of Chapters 1–5 has been sent to your email.</p>
      {mine.preview_url && <a href={mine.preview_url} target="_blank" rel="noreferrer" data-testid="watchers-read-preview-link" className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>READ CHAPTERS 1–5 <ArrowRight className="w-4 h-4" /></a>}
    </div>
  );
  if (!status.open) return (
    <div className="mt-6 rounded-2xl p-6" style={box} data-testid="watchers-gate-closed">
      <div className="eyebrow mb-1" style={{ color: "var(--gold-soft)" }}>🔒 Early access closed</div>
      <p className="font-serif text-lg leading-snug">{status.closed_message || "The first 100 readers are through the gate. The Watchers Are Still Watching is coming October 30, 2026."}</p>
    </div>
  );
  return (
    <div className="mt-6 rounded-2xl p-6" style={box} data-testid="watchers-gate-open">
      <div className="eyebrow mb-1" style={{ color: "var(--gold-soft)" }}>🔓 The gate is open</div>
      <p className="font-serif text-lg leading-snug">Be among the first 100 readers to receive Chapters 1–5 of The Watchers Are Still Watching before release.</p>
      <button data-testid="watchers-open-gate-btn" onClick={onOpen} disabled={busy}
        className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium tracking-wide disabled:opacity-60" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
        {busy ? "Opening…" : "OPEN THE GATE"} <ArrowRight className="w-4 h-4" />
      </button>
      <p className="mt-3 text-xs opacity-85">Free Temple account required · Limited to the first 100 readers</p>
    </div>
  );
}

function ReaderModal({ item: open, onClose, toggleSave, isSaved }) {
  const { user } = useApp();
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" style={{ background: "rgba(48,43,41,0.72)" }} onClick={onClose}>
      <div data-testid="library-reader-modal" onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "var(--bg-cream)" }}>
        <div className="p-6 sm:p-8 overflow-y-auto">
          <div className="flex gap-5">
            {open.art_url && <img src={assetUrl(open.art_url)} alt={open.title} className={`${open.type === "Book" ? "w-28 sm:w-36 aspect-[2/3]" : "w-28 h-28"} object-cover rounded-xl flex-shrink-0`} />}
            <div className="min-w-0">
              <div className="eyebrow mb-2">{PUBLIC_LABEL[open.type]}{isUpcoming(open) ? " · Upcoming" : ""}{open.length ? ` · ${open.length}` : ""}</div>
              <h2 className="font-serif text-2xl sm:text-3xl leading-tight mb-1" style={{ color: "var(--burgundy)" }}>{open.title}</h2>
              {open.subtitle && <p className="font-serif italic" style={{ color: "var(--text-muted)" }}>{open.subtitle}</p>}
              <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>By {open.author}{open.release_date && isUpcoming(open) ? ` · Coming ${fmtDate(open.release_date)}` : ""}</div>
            </div>
            <button data-testid="library-reader-close-btn" onClick={onClose} className="ml-auto w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0" style={{ background: "var(--bg-cream-card)", color: "var(--burgundy)" }}>✕</button>
          </div>
          {open.excerpt && <p className="font-serif italic text-lg leading-snug mt-6" style={{ color: "var(--text-obsidian)" }}>"{open.excerpt}"</p>}
          {open.description && <Markdown className="mt-5" style={{ color: "var(--text-obsidian)" }}>{open.description}</Markdown>}
          {open.body && <Markdown className="mt-5" style={{ color: "var(--text-obsidian)" }}>{open.body}</Markdown>}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button data-testid="library-save-btn" onClick={() => { toggleSave(open, "library"); toast("Saved to My Temple"); }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
              <Bookmark className={`w-4 h-4 ${isSaved(open.id) ? "fill-current" : ""}`} /> Save
            </button>
            {open.type === "Book" ? (
              <>
                <BookAction l={open} />
                {user && open.external_url && !isUpcoming(open) && <span className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="book-continue-note">Continue to Amazon — opens in a new tab.</span>}
              </>
            ) : open.external_url && (
              <a data-testid="library-external-link" href={open.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
                <ExternalLink className="w-4 h-4" /> Open resource
              </a>
            )}
            {open.paperback_url && !isUpcoming(open) && <a href={open.paperback_url} target="_blank" rel="noreferrer" className="text-sm ink-link" style={{ color: "var(--burgundy)" }}>Paperback →</a>}
          </div>
        </div>
      </div>
    </div>
  );
}
